// Time: a machine-readable <time> that reads as a date, a time, or "3 days ago".
//
// The formatting is Intl.DateTimeFormat and Intl.RelativeTimeFormat in the reader's own
// locale, or the one `locale` names — a page in one language wants its dates in that
// language, whatever the browser is set to.
import { I80Element, define, h } from "../base.js";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

const SHORT_DATE = { month: "short", day: "numeric", year: "numeric" };
const TO_THE_MINUTE = { ...SHORT_DATE, hour: "numeric", minute: "numeric" };
const TO_THE_SECOND = { ...TO_THE_MINUTE, second: "numeric", timeZoneName: "short" };

// What each display shows: the absolute format (none means the ISO UTC string), whether the
// absolute part is shown at all, whether the relative part is, and the format the tooltip uses.
const DISPLAYS = {
  "friendly-relative": { format: SHORT_DATE, absolute: true, relative: true, tooltip: TO_THE_SECOND },
  "friendly-local": { format: TO_THE_SECOND, absolute: true, relative: false, tooltip: null },
  "friendly-only": { format: SHORT_DATE, absolute: true, relative: false, tooltip: null },
  relative: { format: null, absolute: false, relative: true, tooltip: TO_THE_MINUTE },
  utc: { format: null, absolute: true, relative: false, tooltip: null },
};
const DISPLAY_NAMES = Object.keys(DISPLAYS);

/** Our own part inside `parent`, never one belonging to a nested <i80-*> child. */
const own = (parent, name) => parent.querySelector(`:scope > [data-i80-part="${name}"]`);

const parse = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(+date) ? null : date;
};

const format = (date, options, locale) => new Intl.DateTimeFormat(locale || navigator.language, options).format(date);

/**
 * The unit the difference reads best in: seconds under a minute, then minutes, hours, days,
 * and weeks past two years — the scale Helios picked.
 */
function relative(ms, locale) {
  const abs = Math.abs(ms);
  const [divisor, unit] = abs < MINUTE ? [SECOND, "second"]
    : abs < HOUR ? [MINUTE, "minute"]
      : abs < DAY ? [HOUR, "hour"]
        : abs < 100 * WEEK ? [DAY, "day"]
          : [WEEK, "week"];
  return new Intl.RelativeTimeFormat(locale || navigator.language).format(Math.trunc(ms / divisor), unit);
}

// "3 minutes ago" stops being true a minute later, so the elements showing a relative time
// share one ticker, running only while at least one of them is on the page.
const ticking = new Set();
let timer = null;

function tick(el, on) {
  if (on) ticking.add(el);
  else ticking.delete(el);
  if (ticking.size && timer === null) timer = setInterval(() => ticking.forEach((e) => e.update()), SECOND);
  if (!ticking.size && timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

/**
 * <i80-time date="2024-10-16T09:30:00Z"></i80-time>
 * <i80-time start="2024-10-14" end="2024-10-16"></i80-time>
 * <i80-time display="relative"><time datetime="2024-10-16T09:30:00Z">16 October 2024</time></i80-time>
 *
 * A real <time datetime> so the date stays machine-readable, showing an absolute date, a
 * relative one, or both. Without `display` it picks for you: a relative time inside the
 * last week, the local date and time before that. A <time datetime> you wrote inside is
 * the date and the element we go on using, so a server-rendered page reads before i80.js
 * loads. The precise date is in a tooltip unless you set tooltip="false".
 */
export class I80Time extends I80Element {
  static get observedAttributes() {
    return ["date", "start", "end", "display", "tooltip", "locale"];
  }

  setup() {
    let single = this.part("single");
    if (!single) {
      // A <time> the server rendered: its datetime is the date, and we keep the element.
      single = [...this.children].find((c) => c.localName === "time");
      single?.setAttribute("data-i80-part", "single");
    }
    this._single = single ?? null;
    this._written = single?.getAttribute("datetime") ?? null;
    this._shape = null;
    // Text the author wrote is a fallback for before i80.js ran; the formatted date replaces it.
    for (const node of this.takeContent()) node.remove();
  }

  adopt() {
    if (!this._single) {
      const single = [...this.children].find((c) => c.localName === "time" && !c.hasAttribute("data-i80-part"));
      if (single) {
        single.setAttribute("data-i80-part", "single");
        this._single = single;
        this._written = single.getAttribute("datetime");
        this._shape = null;
      }
    }
    // Anything else the author wrote is a fallback for before i80.js ran; the date replaces it.
    for (const node of this.takeContent()) node.remove();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    tick(this, false);
  }

  /** The date, or the two ends of a range, from the attributes or the written <time>. */
  get dates() {
    const start = parse(this.getAttribute("start"));
    const end = parse(this.getAttribute("end"));
    if (start && end && start <= end) return { start, end };
    const date = parse(this.getAttribute("date") ?? this._written);
    return date ? { date } : {};
  }

  update() {
    const { date, start, end } = this.dates;
    const shape = date ? "single" : start ? "range" : "none";
    const tooltip = (this.getAttribute("tooltip") ?? "true") !== "false";
    const key = `${shape}:${tooltip}`;
    if (key !== this._shape) {
      this._frame(shape, tooltip);
      this._shape = key;
    }
    if (shape === "single") this._paintSingle(date);
    else if (shape === "range") this._paintRange(start, end);
    tick(this, shape === "single" && this._display(date).relative);
  }

  /** The display options in force: the named one, or relative inside a week and local past it. */
  _display(date) {
    const named = this.hasAttribute("display") ? this.choice("display", DISPLAY_NAMES, "") : "";
    if (named) return DISPLAYS[named];
    return Math.abs(+date - Date.now()) > WEEK ? DISPLAYS["friendly-local"] : DISPLAYS.relative;
  }

  /** The <time> (or the two of a range), on its own or inside the tooltip button. */
  _frame(shape, tooltip) {
    for (const name of ["tip", "single", "range"]) own(this, name)?.remove();
    if (shape === "none") return;
    let el;
    if (shape === "single") {
      el = this._single ??= h("time", { "data-i80-part": "single" });
      el.className = "i80-time i80-time--single";
    } else {
      el = this._rangeEl ??= h("span", { class: "i80-time i80-time--range", "data-i80-part": "range" },
        h("time", { "data-i80-part": "from" }), "–", h("time", { "data-i80-part": "to" }));
    }
    if (tooltip) {
      this.append(h("i80-tooltip", { placement: "bottom", "data-i80-part": "tip" },
        h("button", { type: "button", class: "i80-tooltip-button i80-tooltip-button--is-inline i80-time-wrapper", "data-i80-part": "trigger" }, el)));
    } else {
      this.append(el);
    }
  }

  _tooltip(text) {
    own(this, "tip")?.setAttribute("text", text);
  }

  _paintSingle(date) {
    const options = this._display(date);
    const locale = this.getAttribute("locale");
    const iso = date.toISOString();
    const absolute = options.format ? format(date, options.format, locale) : iso;
    const since = relative(+date - Date.now(), locale);
    this._single.setAttribute("datetime", iso);
    this._single.textContent = options.absolute
      ? (options.relative ? `${absolute} (${since})` : absolute)
      : since;
    this._tooltip(options.tooltip ? format(date, options.tooltip, locale) : iso);
  }

  _paintRange(start, end) {
    // The year is said once when both ends share it.
    const fromEl = this._rangeEl.querySelector('[data-i80-part="from"]');
    const toEl = this._rangeEl.querySelector('[data-i80-part="to"]');
    const locale = this.getAttribute("locale");
    fromEl.setAttribute("datetime", start.toISOString());
    fromEl.textContent = format(start, start.getFullYear() === end.getFullYear() ? { month: "short", day: "numeric" } : SHORT_DATE, locale);
    toEl.setAttribute("datetime", end.toISOString());
    toEl.textContent = format(end, SHORT_DATE, locale);
    this._tooltip(`${start.toISOString()}–${end.toISOString()}`);
  }
}

define("i80-time", I80Time);
